import { t } from '@lingui/core/macro';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import { formatDecimal } from '@luminovo/frontend-utils';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

import { type ComparisonBarDatum } from './ComparisonBarChart';
import { BASELINE_COLOR, formatPct, MONETARY_EPSILON } from './comparisonChartShared';

function ColorSwatch({ color }: { color: string }) {
    return (
        <span
            style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: color,
                flexShrink: 0,
            }}
        />
    );
}

function formatAbs(value: number, fmt: (v: number) => string): string {
    return fmt(Math.abs(value));
}

function netColor(net: number): string {
    if (net < -MONETARY_EPSILON) return colorSystem.green[7];
    if (net > MONETARY_EPSILON) return colorSystem.red[7];
    return colorSystem.neutral[7];
}

function TooltipRow({
    label,
    value,
    color = colorSystem.neutral[8],
}: {
    label: string;
    value: string;
    color?: string;
}) {
    return (
        <Flexbox
            gap={8}
            sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {label}
            </Text>
            <Text variant="body-small-semibold" color={color} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {value}
            </Text>
        </Flexbox>
    );
}

function refFullLabel(datum: ComparisonBarDatum): string {
    // The same message as the app's legend, "Reference {0}".
    return t`Reference ${String.fromCharCode(65 + datum.refIndex)}`;
}

function BaselineColumn({
    baselineTotal,
    baselineColor,
    formatValue,
}: {
    baselineTotal: number;
    baselineColor: string;
    formatValue: (value: number) => string;
}) {
    return (
        <Flexbox
            gap={3}
            style={{ minWidth: 130 }}
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                gap={6}
                style={{ marginBottom: 1 }}
                sx={{
                    alignItems: 'center',
                }}
            >
                <ColorSwatch color={baselineColor} />
                <Text variant="body-small-semibold" color={colorSystem.neutral[8]}>
                    {t`Baseline`}
                </Text>
            </Flexbox>
            <TooltipRow label={t`Volume`} value={formatValue(baselineTotal)} />
        </Flexbox>
    );
}

function RefColumn({ datum, formatValue }: { datum: ComparisonBarDatum; formatValue: (value: number) => string }) {
    const net = datum.increase + datum.decrease;
    const pctLabel = formatPct(net, datum.comparedBaselineTotal);
    const nColor = netColor(net);
    const netFormatted =
        net > MONETARY_EPSILON
            ? `+${formatAbs(net, formatValue)}`
            : net < -MONETARY_EPSILON
              ? `−${formatAbs(net, formatValue)}`
              : formatAbs(net, formatValue);

    return (
        <Flexbox
            gap={3}
            style={{ minWidth: 130 }}
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                gap={6}
                style={{ marginBottom: 1 }}
                sx={{
                    alignItems: 'center',
                }}
            >
                <ColorSwatch color={datum.refColor} />
                <Text variant="body-small-semibold" color={colorSystem.neutral[8]}>
                    {refFullLabel(datum)}
                </Text>
            </Flexbox>
            <TooltipRow label={t`Volume`} value={formatValue(datum.refTotal)} />
            <div style={{ borderTop: `1px solid ${colorSystem.neutral[2]}`, margin: '2px 0' }} />
            <Flexbox
                gap={8}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {/* Stroke 2.5 (1.25px at 12px), as in the change indicator: Lucide's 2 is a 1px line. */}
                    <ArrowUpIcon size={12} strokeWidth={2.5} color={colorSystem.red[6]} />
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {t`Increase`}
                    </Text>
                </Flexbox>
                <Text
                    variant="body-small-semibold"
                    color={colorSystem.red[7]}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                    {formatAbs(datum.increase, formatValue)}
                </Text>
            </Flexbox>
            <Flexbox
                gap={8}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <ArrowDownIcon size={12} strokeWidth={2.5} color={colorSystem.green[6]} />
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {t`Decrease`}
                    </Text>
                </Flexbox>
                <Text
                    variant="body-small-semibold"
                    color={colorSystem.green[7]}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                    {formatAbs(datum.decrease, formatValue)}
                </Text>
            </Flexbox>
            <div style={{ borderTop: `1px solid ${colorSystem.neutral[2]}`, margin: '2px 0' }} />
            <Flexbox
                gap={8}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'baseline',
                    }}
                >
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {t`Net`}
                    </Text>
                    {pctLabel && (
                        <Text variant="body-small" color={nColor} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {pctLabel}
                        </Text>
                    )}
                </Flexbox>
                <Text variant="body-small-semibold" color={nColor} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {netFormatted}
                </Text>
            </Flexbox>
        </Flexbox>
    );
}

export function ComparisonTooltip({
    datums,
    groupLabel,
    groupSubLabel,
    formatValue = (v) => formatDecimal(v),
}: {
    datums: ComparisonBarDatum[];
    groupLabel: string;
    groupSubLabel?: string;
    formatValue?: (value: number) => string;
}) {
    const sorted = [...datums].sort((a, b) => a.refIndex - b.refIndex);
    const baselineTotal = sorted[0]?.baselineTotal ?? 0;

    return (
        <Flexbox
            gap={6}
            style={{
                padding: '4px 2px',
            }}
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                gap={1}
                style={{ marginBottom: 2 }}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <Text variant="body-small-semibold" color={colorSystem.neutral[8]}>
                    {groupLabel}
                </Text>
                {groupSubLabel && (
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {groupSubLabel}
                    </Text>
                )}
            </Flexbox>
            <Flexbox gap={16}>
                <BaselineColumn
                    baselineTotal={baselineTotal}
                    baselineColor={BASELINE_COLOR}
                    formatValue={formatValue}
                />
                {sorted.map((datum) => (
                    <Flexbox key={datum.refIndex} gap={16}>
                        <div
                            style={{
                                width: 1,
                                alignSelf: 'stretch',
                                backgroundColor: colorSystem.neutral[2],
                            }}
                        />
                        <RefColumn datum={datum} formatValue={formatValue} />
                    </Flexbox>
                ))}
            </Flexbox>
        </Flexbox>
    );
}
